"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRouter } from "next/navigation";
import { apiService, HomePageData } from "@/lib/api";
import { useGameLaunch } from "@/hooks/useGameLaunch";
import Header from "@/components/Header";
import Banner from "@/components/Banner";
import GameSection from "@/components/GameSection";
import MarqueeBanner from "@/components/MarqueeBanner";
import CategorySection from "@/components/CategorySection";
import CashInModal from "@/components/CashInModal";
import CashOutModal from "@/components/CashOutModal";
import LogoutModal from "@/components/LogoutModal";
import MobileBottomNav from "@/components/MobileBottomNav";
import { toast } from "react-toastify";

export default function Home() {
  const { user, isAuthenticated, logout, refreshBalance } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const { launchingGames, canLaunchGame, handleGameLaunch } = useGameLaunch();
  
  const [homeData, setHomeData] = useState<HomePageData | null>(null);
  const [imgPrefix, setImgPrefix] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedPopup, setSelectedPopup] = useState<unknown>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCashInModal, setShowCashInModal] = useState(false);
  const [showCashOutModal, setShowCashOutModal] = useState(false);
  const [cashInForm, setCashInForm] = useState({
    payment_id: "",
    amount: "",
    transaction_id: "",
    holder_phone: "",
  });
  const [cashOutForm, setCashOutForm] = useState({
    amount: "",
    password: "",
  });

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await apiService.getHomePageData();
        if (response.success) {
          setHomeData(response.data);
          setImgPrefix(response.img_pre_fix);

          if (response.data.u_popup && response.data.u_popup.length > 0) {
            setSelectedPopup(response.data.u_popup[0]);
            setShowPopup(true);
          }
        }
      } catch (error) {
        console.error("Failed to fetch home data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
  };

  const handleCashIn = async () => {
    await refreshBalance();
    router.push("/cashin");
  };

  const handleCashOut = async () => {
    try {
      await refreshBalance();
      const walletsResponse = await apiService.getWallets();
      if (walletsResponse.success && walletsResponse.data.wallets.length > 0) {
        router.push('/cashout');
      } else {
        router.push('/wallet');
      }
    } catch (error) {
      console.error('Failed to check wallets:', error);
      router.push('/wallet');
    }
  };

  const handleCashInSubmit = async () => {
    if (!cashInForm.payment_id || !cashInForm.amount || !cashInForm.transaction_id || !cashInForm.holder_phone) {
      return;
    }

    try {
      const response = await apiService.cashIn({
        payment_id: parseInt(cashInForm.payment_id),
        amount: parseFloat(cashInForm.amount),
        transaction_id: cashInForm.transaction_id,
        holder_phone: cashInForm.holder_phone,
      });

      if (response.success) {
        await refreshBalance();
        setShowCashInModal(false);
        setCashInForm({
          payment_id: "",
          amount: "",
          transaction_id: "",
          holder_phone: "",
        });
      }
    } catch (error) {
      console.error("Cash In failed:", error);
    }
  };

  const handleCashOutSubmit = async () => {
    if (!cashOutForm.amount || !cashOutForm.password) {
      return;
    }

    try {
      const response = await apiService.cashOut({
        amount: parseFloat(cashOutForm.amount),
        password: cashOutForm.password,
      });

      if (response.success) {
        await refreshBalance();
        setShowCashOutModal(false);
        setCashOutForm({
          amount: "",
          password: "",
        });
      }
    } catch (error) {
      console.error("Cash Out failed:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('cashIn')) {
    setCashInForm(prev => ({
      ...prev,
        [name.replace('cashIn', '').toLowerCase()]: value
    }));
    } else if (name.startsWith('cashOut')) {
    setCashOutForm(prev => ({
      ...prev,
        [name.replace('cashOut', '').toLowerCase()]: value
      }));
    }
  };

  const getLotteryRoute = (lotteryId: number) => {
    console.log('getLotteryRoute called with ID:', lotteryId);
    switch (lotteryId) {
      case 1:
        console.log('Routing to /lottery-2d');
        return '/lottery-2d';
      case 2:
        console.log('Routing to /lottery-3d');
        return '/lottery-3d';
      case 3:
        console.log('Routing to /lottery-c2d');
        return '/lottery-c2d';
      case 4:
        console.log('Routing to /lottery-1d');
        return '/lottery-1d';
      case 5:
        console.log('Routing to /lottery-c1d');
        return '/lottery-c1d';
      default:
        console.log('Default routing to /lottery-2d');
        return '/lottery-2d'; // Default fallback
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  return (
    <div className="home-page">
      <Header
        isAuthenticated={isAuthenticated}
        user={user}
        onLogout={() => setShowLogoutModal(true)}
        onCashIn={handleCashIn}
        onCashOut={handleCashOut}
      />

      <MarqueeBanner text={homeData?.u_header?.text || ''} />

      <main className="main-content-full">
          <div className="content-container">
          <Banner banners={homeData?.u_banner || []} />

          <CategorySection 
            categories={homeData?.u_gamecategory || []} 
            imgPrefix={imgPrefix} 
          />

          <div className="games-container">
            <GameSection
              title={t('lottery_games')}
              games={homeData?.u_lottery || []}
              launchingGames={launchingGames}
              canLaunchGame={canLaunchGame}
              onGameLaunch={handleGameLaunch}
              getLotteryRoute={getLotteryRoute}
              isLottery={true}
              sectionType="lottery"
            />

            <GameSection
              title={t('buffalo_games')}
              games={homeData?.u_buffalo_games || []}
              launchingGames={launchingGames}
              canLaunchGame={canLaunchGame}
              onGameLaunch={handleGameLaunch}
              sectionType="buffalo"
            />

            <GameSection
              title={t('popular_games')}
              games={homeData?.u_hot_game || []}
              launchingGames={launchingGames}
              canLaunchGame={canLaunchGame}
              onGameLaunch={handleGameLaunch}
              sectionType="hot"
            />

            <GameSection
              title={t('new_games')}
              games={homeData?.u_new_game || []}
              launchingGames={launchingGames}
              canLaunchGame={canLaunchGame}
              onGameLaunch={handleGameLaunch}
              sectionType="new"
            />
          </div>
        </div>
      </main>

      <CashInModal
          isOpen={showCashInModal}
          onClose={() => setShowCashInModal(false)}
        formData={cashInForm}
        onInputChange={handleInputChange}
        onSubmit={handleCashInSubmit}
      />

      <CashOutModal
        isOpen={showCashOutModal}
        onClose={() => setShowCashOutModal(false)}
        formData={cashOutForm}
        onInputChange={handleInputChange}
        onSubmit={handleCashOutSubmit}
      />

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      <MobileBottomNav isAuthenticated={isAuthenticated} />
    </div>
  );
}